<template>
  <Layout>
    <template #header-container>
      <PageHeader
        isSticky
        hasBreadcrumb
        hasBackButton
        buttonText="Kembali"
        pageType="transaction"
        :title="title"
      >
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButton
              v-if="status.name === 'Tidak Aktif'"
              variant="secondary-red"
              icon="icon-outline_trash"
              :text="$t('global.delete')"
              :disabled="isLoading || hasChanged"
              @click="isOpenDeleteModal = true"
            />

            <McConfirmation
              v-model="isOpenDeleteModal"
              title="Hapus"
              okTitle="Hapus"
              centered
              no-close-on-backdrop
              no-close-on-esc
              @cancel="isOpenDeleteModal = false"
              @handle-ok="handleDelete"
            >
              <p>
                Apakah Anda yakin ingin menghapus penjadwalan
                <strong>{{ title }}</strong
                >?
              </p>
            </McConfirmation>

            <TransitionButton
              :status="status.name"
              :name="showResponse.name"
              :isLoading="isLoading || hasChanged"
              :errorResponse="errorResponse"
              @update:isLoading="isLoading = $event"
              @show-data="showData"
              @assign-error="handleAssignError"
            />
          </div>
        </template>

        <template #banner>
          <Banner
            v-if="isLoading === false"
            :showResponse="showResponse"
            :errorResponse="errorResponse"
          />
        </template>
      </PageHeader>
    </template>

    <div class="delivery-scheduler__header">
      <h4 class="card-title">Detail Penjadwalan</h4>

      <div class="d-flex gap-2">
        <McButton
          variant="secondary"
          icon="icon-outline_close"
          :text="$t('global.action.cancel')"
          :disabled="!hasChanged || isLoading"
          @click="isOpenCancelModal = true"
        />
        <McButton
          icon="icon-outline_save"
          :text="this.$t('global.save')"
          :disabled="!hasChanged || isLoading"
          @click="handleSubmit"
        />
      </div>

      <McConfirmation
        v-model="isOpenCancelModal"
        title="Batal"
        okTitle="Ya"
        cancelTitle="Tidak"
        centered
        no-close-on-backdrop
        no-close-on-esc
        @cancel="isOpenCancelModal = false"
        @handle-ok="handleCancel"
      >
        <p>
          {{ $t('global.text.modal.cancelChange') }}
          <strong>Informasi Penjadwalan</strong>?
        </p>
      </McConfirmation>
    </div>

    <b-card>
      <FormulateForm
        v-model="formValues"
        ref="form-ref"
        :errors="formErrors"
        @input="handleInputForm"
      >
        <BasicInfoSection :showResponse="showResponse" />

        <hr />

        <h4 class="card-title">Template Pengiriman</h4>
        <TemplateInfoSection :fullObjects.sync="templateFullObjects" />

        <hr />

        <h4 class="card-title">Perulangan</h4>
        <RecurrenceInfoSection
          :formValues="formValues"
          :recurrenceData="recurrenceData"
          :status="status.name"
        >
          <template #weekly-checkbox>
            <div v-for="item in dayItems" class="d-flex gap-1">
              <McCheckbox
                v-model="formValues.selectedWeeklyDays"
                :id="item.key"
                :value="item.key"
                :disabled="isLoading"
              />
              <label class="m-0" :for="item.key">
                {{ item.label }}
              </label>
            </div>
          </template>
        </RecurrenceInfoSection>
      </FormulateForm>
    </b-card>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { computed } from 'vue'
import { rrulestr } from 'rrule'
import moment from 'moment'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import McConfirmation from '@/components/McConfirmation'

import Banner from './components/Banner'
import TransitionButton from './components/TransitionButton'
import BasicInfoSection from './components/BasicInfoSection'
import TemplateInfoSection from './components/TemplateInfoSection'
import RecurrenceInfoSection from './components/RecurrenceInfoSection'

import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import omitBy from 'lodash/omitBy'

import { debounce } from '@/helpers/debounce'

import { formValuesToRruleString } from './helpers/rrule.js'
import * as DeliveryScheduler from '@/services/rest-api/lib/scheduler/scheduler'
import {
  transitionMappings,
  freqMappings,
  dayItems,
  weekdayMappings,
} from './lib/constants/mappings.js'
import errorMappings from './lib/constants/error-mappings.js'
import { errorV2Mappings } from './lib/constants/error-v2-mappings'

export default {
  page: {
    title: 'Delivery Scheduler',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McCheckbox,
    McConfirmation,
    Banner,
    TransitionButton,
    BasicInfoSection,
    TemplateInfoSection,
    RecurrenceInfoSection,
  },
  provide() {
    return {
      isLoading: computed(() => this.isLoading),
    }
  },
  data() {
    return {
      transitionMappings,
      freqMappings,
      dayItems,
      weekdayMappings,

      isLoading: false,
      isOpenCancelModal: false,
      isOpenDeleteModal: false,
      recurrenceTrans: '',

      showResponse: {},
      errorResponse: {},
      formValues: {},
      invalidFormValues: {},
      initialFormValues: {},
      templateFullObjects: {},
      recurrenceData: {},
      formErrors: {},
    }
  },
  computed: {
    title() {
      return this.showResponse.name || ''
    },
    schedulerId() {
      return this.$route.params.id
    },
    status() {
      return this.showResponse.status || ''
    },
    initialRule() {
      return this.showResponse.recurrence
    },
    hasChanged() {
      return !isEqual(this.formValues, this.initialFormValues)
    },
  },
  watch: {
    formValues(newValue) {
      if (Object.keys(this.formErrors).length === 0) return
      if (this.isLoading) return

      for (const [key, _] of Object.entries(this.invalidFormValues)) {
        if (this.invalidFormValues[key] === newValue[key]) continue

        this.$delete(this.formErrors, key)
        this.$delete(this.invalidFormValues, key)
      }
    },
  },
  mounted() {
    if (this.schedulerId === undefined) return

    this.showData()
  },
  methods: {
    async showData() {
      this.isLoading = true

      const res = await DeliveryScheduler.show(this.schedulerId)

      this.errorResponse = {}

      if (res.errors?.code) {
        this.errorResponse = res.errors

        if (res.errors?.code.includes('ERR_INVALID_TEMPLATE')) {
          this.$set(
            this.formErrors,
            'template_ids',
            'Ada data pengiriman yang telah dihapus dari sistem'
          )
          this.$set(this.invalidFormValues, 'template_ids', this.formValues.template_ids)
        }
      }

      this.showResponse = res.data
      await this.$nextTick()

      this.parseValues()
      await this.$nextTick()

      this.isLoading = false
    },

    parseValues() {
      if (this.showResponse.templates.length === 0) {
        this.$set(
          this.formErrors,
          'template_ids',
          'Ada data pengiriman yang telah dihapus dari sistem'
        )
        this.$set(this.invalidFormValues, 'template_ids', this.formValues.template_ids)
      } else {
        this.templateFullObjects = this.showResponse.templates[0]
      }

      const data = {
        name: this.showResponse.name,
        template_ids: this.templateFullObjects?.id,
      }

      this.formValues = data
      this.recurrenceTrans = this.showResponse.recurrence_translate

      const rruleObj = rrulestr(this.showResponse.recurrence)
      const rruleOptions = rruleObj.origOptions

      const endDate = moment(rruleOptions.until)

      data.recurrenceType = this.freqMappings[rruleOptions.freq]
      data.recurrenceValue = rruleOptions.interval
      data.recurrenceTime = endDate.format('HH:mm')
      data.selectedWeeklyDays = ['MO']
      data.selectedMonthlyDate = 1

      data.endDate = endDate.hours(0).minutes(0).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      data.startDate = moment(rruleOptions.dtstart.setHours(0, 0, 0, 0)).format(
        'YYYY-MM-DDTHH:mm:ss.SSSZ'
      )

      if (data.recurrenceType === 'WEEKLY') {
        data.selectedWeeklyDays = rruleOptions.byweekday.map((item) => {
          return this.weekdayMappings[item.weekday]
        })
      }

      if (data.recurrenceType === 'MONTHLY') {
        data.selectedMonthlyDate = rruleOptions.bymonthday
      }

      this.formValues = data
      this.initialFormValues = cloneDeep(data)

      this.recurrenceData = {
        recurrence_translate: this.showResponse.recurrence_translate,
        next_run: this.showResponse.next_run,
      }
    },

    handleInputForm: debounce(async function (data) {
      if (this.isLoading) return

      const rule = formValuesToRruleString(data)
      const res = await DeliveryScheduler.helper({ rule })

      if (res.errors) {
        this.isLoading = false
        return
      }

      this.recurrenceData = res.data
    }, 500),

    handleCancel() {
      this.formValues = cloneDeep(this.initialFormValues)
      this.isOpenCancelModal = false
    },

    filterUnchangedData() {
      const keys = ['name', 'template_ids']

      let newValue = {}
      let hasChanged = false

      keys.forEach((key) => {
        if (isEqual(this.formValues[key], this.initialFormValues[key])) return

        if (key === 'template_ids') {
          newValue[key] = [this.formValues[key]]
          newValue.template_type = 'delivery-order'
          return
        }

        newValue[key] = this.formValues[key]
      })

      const ruleChanged = omitBy(this.formValues, (value, key) => {
        if (keys.includes(key)) return true

        return isEqual(value, this.initialFormValues[key])
      })

      if (Object.keys(ruleChanged).length > 0) {
        newValue.recurrence = formValuesToRruleString(this.formValues)
      }

      if (Object.keys(newValue).length > 0) {
        hasChanged = true
      }

      return [hasChanged, newValue]
    },

    async handleSubmit() {
      if (this.$refs['form-ref'].hasErrors) {
        this.$refs['form-ref'].showErrors()
        this.$mcToast.error('Terdapat kesalahan format pada input anda')

        return
      }

      const [hasChanged, data] = this.filterUnchangedData()
      if (!hasChanged) return

      this.isLoading = true

      const res = await DeliveryScheduler.update(this.schedulerId, data)

      if (res.errors_v2) {
        var errorv2 = errorV2Mappings[res.errors_v2.issues?.[0]?.code]
        if (errorv2) {
          this.$mcToast.error(errorv2)
          this.isLoading = false
          return
        }
      }

      if (res.errors) {
        if (res.errors.templates || res.errors.template_ids) {
          this.$mcToast.error('Gagal disimpan. Ada kesalahan pada template.')
        } else {
          this.$mcToast.error('Terdapat kesalahan pada input Anda')
        }

        this.handleAssignError(res.errors)
        this.isLoading = false
        return
      }

      this.$mcToast.success('Berhasil disimpan.')
      this.isLoading = false

      this.formValues = {}
      this.initialFormValues = {}
      await this.$nextTick()

      await this.showData()
    },

    async handleDelete() {
      if (this.isLoading || this.hasChanged) return

      this.isLoading = true

      const res = await DeliveryScheduler.deleteMany({ ids: [this.schedulerId] })

      if (res.errors) {
        this.$mcToast.error('Gagal dihapus.')
        this.isLoading = false
        this.isOpenDeleteModal = false
        return
      }

      this.isOpenDeleteModal = false

      this.$mcToast.success('Penjadwalan berhasil dihapus.')
      this.$router.push({ name: 'Delivery Scheduler' })
    },

    handleAssignError(errors) {
      if (Object.keys(errors).length === 0) return

      for (const [key, value] of Object.entries(errors)) {
        if (['templates', 'template_ids'].includes(key)) {
          this.$set(
            this.formErrors,
            'template_ids',
            'Ada data pengiriman yang telah dihapus dari sistem'
          )
          this.$set(this.invalidFormValues, key, this.formValues.template_ids)

          continue
        }

        let message = errorMappings[key][value[0]]
        this.$set(this.formErrors, key, message)
        this.$set(this.invalidFormValues, key, this.formValues[key])
      }
    },
  },
}
</script>

<style lang="scss">
.delivery-scheduler {
  &__header {
    display: flex;
    justify-content: space-between;

    margin: 24px 0 12px;
  }
}
</style>
